/**
 * L'EXPORT D'UNE COPIE ANNOTÉE — ce que « exporter en PDF » veut dire ici.
 *
 * Le fondateur, en recette (14 septembre 2026) : « impossible de charger les
 * devoirs de la section apprentissage annotés pour les visualiser. On voit
 * juste la liste. Et donc pas possible d'exporter en PDF, il faut s'assurer que
 * les versions sont cohérentes. »
 *
 * ─── Pourquoi l'impression du navigateur, et PAS une route qui rend un PDF ──
 *
 * Un PDF produit par le serveur serait une SECONDE mise en page de la copie, à
 * tenir à jour en parallèle de celle de l'écran. C'est exactement ce que le
 * fondateur demande d'éviter — « s'assurer que les versions sont cohérentes » :
 * deux rendus divergent au premier changement, et la copie imprimée cesserait
 * de dire ce que l'élève lit dans son application.
 *
 * L'impression du navigateur rend LA PAGE ELLE-MÊME. Il n'y a donc qu'une seule
 * mise en page, et « Enregistrer en PDF » est une destination d'impression que
 * tous les navigateurs offrent. Ce module ne contient que ce qui est calculable
 * et donc éprouvable : le nom du document, qui devient le nom du fichier
 * proposé par le navigateur.
 */
import type { CopieDto } from '@silex/shared';

/** Les caractères qu'un nom de fichier ne doit pas porter, accents mis à plat. */
function ardoise(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/** La date au format ISO court, dans le fuseau de l'utilisateur. */
function jourDe(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'sans-date';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Le titre du document pendant l'impression — donc le nom de fichier que le
 * navigateur propose par défaut.
 *
 * Il porte le prénom de l'élève quand la page le connaît, le titre du travail
 * et sa date : un tuteur qui exporte trois copies d'affilée doit pouvoir les
 * distinguer dans son dossier de téléchargement sans les rouvrir.
 */
export function nomDocumentCopie(copie: CopieDto, prenom?: string | null): string {
  const morceaux = [
    'copie',
    prenom ? ardoise(prenom) : null,
    ardoise(copie.titre ?? 'travail'),
    jourDe(copie.creeLe),
  ].filter((m): m is string => Boolean(m) && m !== '');
  return morceaux.join('-');
}
